import React from "react";
import { useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useActiveOrg } from "context/ActiveOrgContext";
import Page from "components/Page/Page";
import Button from "components/Button/Regular";
import ConfirmDialog from "components/ConfirmDialog";
import vgToast from "utils/toast";
import { formatMoney } from "constants/currency";
import {
  GroupedLine,
  Invoice,
  getInvoiceStateLabel,
  exportInvoicePdf,
  groupLines,
  readInvoice,
  removeLines,
} from "services/invoices.service";
import { Product, readOrgProducts } from "services/products.service";
import {
  BookingConfig,
  readBookableConfigs,
} from "services/booking-configs.service";
import { Payment, mapPayment, outstanding } from "services/payments.service";
import { ACTIVATION_STATE } from "constants/bookingEnums";
import AddProductLine from "./AddProductLine";
import AddCustomLine from "./AddCustomLine";
import RecordPayment from "./RecordPayment";

/**
 * One invoice: what is on it, what it comes to, and the two things a clinic
 * needs to do with it - add to it, and give the client a copy.
 *
 * The client's copy is a PDF built by OlaCore, not by this screen. Two documents
 * that are meant to be the same invoice will eventually differ, and the one that
 * matters is the one the server can also email. So this screen is for working on
 * the invoice; the PDF is the artefact.
 */

function formatDate(value: string | undefined) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

const InvoiceDetails = () => {
  const { id } = useParams();
  const { orgId } = useActiveOrg();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const invoiceId = id ? Number(id) : NaN;
  const canLoad =
    isAuthenticated && orgId > 0 && Number.isFinite(invoiceId) && invoiceId > 0;

  const [invoice, setInvoice] = React.useState<Invoice | null>(null);
  const [products, setProducts] = React.useState<Product[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [configs, setConfigs] = React.useState<BookingConfig[]>([]);
  const [payments, setPayments] = React.useState<Payment[]>([]);
  const [adding, setAdding] = React.useState(false);
  const [addingCharge, setAddingCharge] = React.useState(false);
  const [paying, setPaying] = React.useState(false);
  const [toRemove, setToRemove] = React.useState<GroupedLine | null>(null);
  const [removing, setRemoving] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!canLoad) {
      setInvoice(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const [invoiceRes, productRes, configRes] = await Promise.all([
        readInvoice(token, orgId, invoiceId),
        readOrgProducts(token, orgId),
        readBookableConfigs(token, orgId),
      ]);

      if (invoiceRes.error) {
        setError(invoiceRes.error.message);
        setInvoice(null);
        return;
      }
      setInvoice(invoiceRes.data);
      // A failed product read is not a failed invoice: the invoice still shows,
      // only the add-a-product control has nothing to offer.
      setProducts(productRes.data ?? []);
      setConfigs(
        (configRes.data ?? []).filter((c) => c.isActive === ACTIVATION_STATE.ACTIVE)
      );
      setPayments((invoiceRes.data?.payments ?? []).map(mapPayment));
    } catch (e) {
      setError((e as Error).message);
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  }, [canLoad, getAccessTokenSilently, orgId, invoiceId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const productName = React.useCallback(
    (productId: number | undefined) =>
      products.find((p) => p.productId === productId)?.name,
    [products]
  );

  const lines: GroupedLine[] = React.useMemo(
    () =>
      groupLines(invoice?.lines ?? [], (line) =>
        line.productId
          ? productName(line.productId) ?? `Product ${line.productId}`
          : line.description ?? // a custom charge, from its order's metadata
            (line.serviceOrderId ? `Booking ${line.serviceOrderId}` : "Item")
      ),
    [invoice, productName]
  );

  // Summed from the lines rather than trusting the header: a total that
  // disagrees with the lines under it is the one number nobody can explain.
  const linesTotal = lines.reduce((sum, line) => sum + (line.total ?? 0), 0);
  const currency = invoice?.monetaryCurrency ?? lines[0]?.currency;

  // From the payments listed beside it, not the invoice's stored balance, which
  // is only as fresh as the last read.
  const paidTotal = payments.reduce((sum, p) => sum + (p.amount ?? 0), 0);
  const owed = outstanding(linesTotal, payments);

  const downloadPdf = async () => {
    setExporting(true);
    try {
      const token = await getAccessTokenSilently();
      const { data, error: apiError } = await exportInvoicePdf(token, orgId, invoiceId);
      if (apiError || !data) {
        vgToast.error(apiError?.message ?? "The invoice PDF could not be produced.");
        return;
      }

      // Named after the reference rather than the id, because that is what the
      // client sees on the document and will quote back.
      const name = `invoice-${invoice?.reference?.slice(0, 8) ?? invoiceId}.pdf`;
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = name;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      vgToast.error((e as Error).message);
    } finally {
      setExporting(false);
    }
  };

  const confirmRemove = async () => {
    if (!toRemove) return;
    setRemoving(true);
    try {
      const token = await getAccessTokenSilently();
      const { error: apiError } = await removeLines(
        token,
        orgId,
        toRemove.invoiceableItemIds
      );
      if (apiError) {
        vgToast.error(apiError.message);
        return;
      }
      vgToast.success("Line removed.");
      setToRemove(null);
      await load();
    } catch (e) {
      vgToast.error((e as Error).message);
    } finally {
      setRemoving(false);
    }
  };

  if (loading && !invoice) {
    return (
      <Page>
        <div className="p-8 text-sm text-gray-500">Loading invoice…</div>
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <div className="p-8 text-sm text-red-500">{error}</div>
      </Page>
    );
  }

  if (!invoice) {
    return (
      <Page>
        <div className="p-8 text-sm text-gray-500">That invoice was not found.</div>
      </Page>
    );
  }

  return (
    <Page>
      <div className="mx-auto w-full max-w-4xl">
        {/* Controls, deliberately outside the printable area. */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 print:hidden">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">
              Invoice {invoice.reference ? invoice.reference.slice(0, 8) : invoice.invoiceId}
            </h1>
            <p className="text-sm text-slate-500">
              {getInvoiceStateLabel(invoice.invoiceState)}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button color="Secondary" onClick={downloadPdf} disabled={exporting}>
              <span>{exporting ? "Preparing…" : "Download PDF"}</span>
            </Button>
            <Button color="Secondary" onClick={() => setAddingCharge(true)}>
              <span>Add charge</span>
            </Button>
            <Button color="Secondary" onClick={() => setAdding(true)}>
              <span>Add item</span>
            </Button>
            <Button color="Primary" onClick={() => setPaying(true)}>
              <span>Record payment</span>
            </Button>
          </div>
        </div>

        <article
          id="invoice-printable"
          className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10 print:border-0 print:shadow-none print:p-0"
        >
          <header className="mb-8 flex flex-wrap justify-between gap-6 border-b border-slate-100 pb-6">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">Invoice</h2>
              <dl className="mt-2 text-sm text-slate-600">
                <div className="flex gap-2">
                  <dt className="text-slate-400">Reference</dt>
                  <dd>{invoice.reference ?? invoice.invoiceId}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-slate-400">Raised</dt>
                  <dd>{formatDate(invoice.createdOn_UTC)}</dd>
                </div>
                {invoice.deadline_UTC && (
                  <div className="flex gap-2">
                    <dt className="text-slate-400">Due</dt>
                    <dd>{formatDate(invoice.deadline_UTC)}</dd>
                  </div>
                )}
              </dl>
            </div>
            <div className="text-sm text-slate-600 sm:text-right">
              <p className="text-slate-400">Billed to</p>
              <p className="font-medium text-slate-900">
                {invoice.contactFullName ?? `Client ${invoice.contactId ?? "—"}`}
              </p>
            </div>
          </header>

          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-slate-400">
                <th className="pb-2 font-medium">Item</th>
                <th className="pb-2 text-right font-medium">Qty</th>
                <th className="pb-2 text-right font-medium">Unit</th>
                <th className="pb-2 text-right font-medium">Amount</th>
                <th className="pb-2 print:hidden" />
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-slate-500">
                    Nothing on this invoice yet.
                  </td>
                </tr>
              )}
              {lines.map((line) => (
                <tr key={line.key} className="border-b border-slate-50">
                  <td className="py-3 text-slate-800">{line.description}</td>
                  <td className="py-3 text-right text-slate-600">{line.quantity}</td>
                  <td className="py-3 text-right text-slate-600">
                    {formatMoney(line.unitAmount, line.currency ?? currency)}
                  </td>
                  <td className="py-3 text-right font-medium text-slate-900">
                    {formatMoney(line.total, line.currency ?? currency)}
                  </td>
                  <td className="py-3 text-right print:hidden">
                    <button
                      type="button"
                      className="text-xs text-slate-400 hover:text-red-500"
                      onClick={() => setToRemove(line)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3} className="pt-4 text-right text-slate-500">
                  Total
                </td>
                <td className="pt-4 text-right text-lg font-semibold text-slate-900">
                  {formatMoney(linesTotal, currency)}
                </td>
                <td className="print:hidden" />
              </tr>
              {payments.length > 0 && (
                <>
                  <tr>
                    <td colSpan={3} className="pt-1 text-right text-slate-500">
                      Paid
                    </td>
                    <td className="pt-1 text-right text-slate-600">
                      {formatMoney(paidTotal, currency)}
                    </td>
                    <td className="print:hidden" />
                  </tr>
                  <tr>
                    <td colSpan={3} className="pt-1 text-right text-slate-500">
                      {owed != null && owed < 0 ? "Credit" : "Outstanding"}
                    </td>
                    <td className="pt-1 text-right font-medium text-slate-900">
                      {formatMoney(owed != null ? Math.abs(owed) : undefined, currency)}
                    </td>
                    <td className="print:hidden" />
                  </tr>
                </>
              )}
            </tfoot>
          </table>
        </article>
      </div>

      <AddProductLine
        open={adding}
        orgId={orgId}
        invoiceId={invoiceId}
        products={products}
        fallbackCurrency={currency}
        onCancel={() => setAdding(false)}
        onAdded={async () => {
          setAdding(false);
          await load();
        }}
      />

      <AddCustomLine
        open={addingCharge}
        orgId={orgId}
        invoiceId={invoiceId}
        contactId={invoice.contactId}
        currency={currency}
        configs={configs}
        onCancel={() => setAddingCharge(false)}
        onAdded={async () => {
          setAddingCharge(false);
          await load();
        }}
      />

      <RecordPayment
        open={paying}
        orgId={orgId}
        invoiceId={invoiceId}
        contactId={invoice.contactId}
        outstanding={owed}
        currency={currency}
        onCancel={() => setPaying(false)}
        onRecorded={async () => {
          setPaying(false);
          await load();
        }}
      />

      <ConfirmDialog
        open={toRemove !== null}
        title="Remove this line?"
        description={
          toRemove
            ? `This removes ${toRemove.quantity} × ${toRemove.description} from the invoice.`
            : ""
        }
        confirmLabel="Remove"
        busy={removing}
        onCancel={() => {
          if (!removing) setToRemove(null);
        }}
        onConfirm={confirmRemove}
      />
    </Page>
  );
};

export default InvoiceDetails;
